import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { ApiFehler, hole, schicke } from '../lib/api'
import { datumHeute } from '../lib/datum'
import type { MitgliedKurz } from '../lib/modelle'
import {
  WOCHENTAG_NAME,
  aktuellerPlan,
  alsStundenBody,
  gueltigkeitText,
  sichtbareTage,
  stundenProTag,
  wocheHeute,
  zeitSpanne,
  type Fach,
  type Stunde,
  type Stundenplan as Plan,
  type Woche,
} from '../lib/stundenplan'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import {
  Button,
  Card,
  Chip,
  Empty,
  IconButton,
  ListItem,
  SegmentedButtons,
  Select,
  Sheet,
  TextField,
} from '../ui'

interface Vorlage {
  id: string
  name: string
  isSystem: boolean
}

interface NeuerPlan {
  memberId: string
  name: string
  validFrom: string
  validUntil: string
  weekPattern: 'single' | 'ab'
  abAnchorMonday: string
}

interface NeueStunde {
  weekday: number
  week: '' | Woche
  startsAt: string
  endsAt: string
  subjectId: string
  neuesFach: string
  room: string
}

const LEERE_STUNDE: NeueStunde = {
  weekday: 1,
  week: '',
  startsAt: '08:00',
  endsAt: '08:45',
  subjectId: '',
  neuesFach: '',
  room: '',
}

/**
 * Der Stundenplan - je Kind einer, mit A/B-Wochen und Schuljahr.
 *
 * Wer hier landet, will EINE Frage beantwortet haben: was hat mein Kind wann.
 * Deshalb steht die Woche oben und alles Verwalten (Plan anlegen, Stunde
 * eintragen, Plan bearbeiten) darunter, aufklappbar.
 */
export function Stundenplan() {
  const qc = useQueryClient()
  const { abfrage } = useSitzung()
  const preview = Boolean(abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview)
  const darfAnlegen = useDarf('timetable.create') && !preview
  const darfAendern = useDarf('timetable.edit') && !preview
  const darfArchivieren = useDarf('timetable.delete') && !preview
  const ich = abfrage.data && !abfrage.data.setupRequired ? abfrage.data.member.id : ''

  const [params, setParams] = useSearchParams()
  const [fehler, setFehler] = useState<string | null>(null)
  const [gewaehlteWoche, setGewaehlteWoche] = useState<Woche | null>(null)
  const [planId, setPlanId] = useState<string | null>(null)
  const heute = datumHeute()

  const mitglieder = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<MitgliedKurz[]>('/api/members'),
  })
  const plaene = useQuery({
    queryKey: ['timetable', 'list'],
    queryFn: () => hole<{ timetables: Plan[] }>('/api/timetable'),
  })
  const faecher = useQuery({
    queryKey: ['timetable', 'subjects'],
    queryFn: () => hole<{ subjects: Fach[] }>('/api/timetable/subjects'),
  })
  const vorlagen = useQuery({
    queryKey: ['timetable', 'templates'],
    queryFn: () => hole<{ templates: Vorlage[] }>('/api/timetable/templates'),
    enabled: darfAnlegen,
  })

  const personen = mitglieder.data ?? []
  // Wer überhaupt einen Stundenplan haben kann. Gäste nicht, Ausgeschiedene nicht.
  const kandidaten = personen.filter((m) => m.role !== 'guest' && m.status === 'active')
  const alle = plaene.data?.timetables ?? []
  const mitPlan = personen.filter((m) => alle.some((p) => p.memberId === m.id))

  /**
   * Wer in der Kind-Auswahl steht.
   *
   * ⚠️ Bis zum 16.09. stand hier `mitPlan` — nur wer SCHON einen Plan hatte.
   * Damit war ein zweites Kind nicht erreichbar: es tauchte nicht in der
   * Auswahl auf, blieb also nie „gewählt", und weil der schwebende Knopf
   * „anlegen" nur ohne sichtbaren Plan anbot, verschwand auch der Weg dorthin.
   * Von außen sah das aus wie „es geht nur EIN Stundenplan" — Datenmodell und
   * API waren die ganze Zeit kindbezogen.
   *
   * Wer anlegen darf, sieht deshalb alle in Frage kommenden Personen
   * (`kandidaten` — dieselbe Liste, aus der auch der Anlegen-Dialog wählt; EIN
   * Begriff, nicht zwei). Wer nur lesen darf, sieht die mit Plan: für ihn hat
   * ein Kind ohne Plan nichts anzuzeigen.
   */
  const auswahl = darfAnlegen ? kandidaten : mitPlan
  // Vorauswahl: ?mitglied=, sonst ich selbst, sonst das erste Kind MIT Plan,
  // sonst der erste Kandidat. ⚠️ Die Stufe „erstes Kind mit Plan" steht vor
  // „erster Kandidat": wer die Seite öffnet, will einen Plan sehen, keine
  // leere Fläche.
  const gewuenscht = params.get('mitglied')
  // ⚠️ Solange NIEMAND einen Plan hat, wird ein schulpflichtiges Mitglied
  // vorgewählt - nicht die erste beste Person. Auf Produktion sind genau so
  // zwei Pläne beim Erwachsenen gelandet statt beim Kind: die Vorbelegung fiel
  // auf `kandidaten[0]`, und `/api/members` sortiert gar nicht.
  const schulpflichtig = (m: MitgliedKurz) => m.role === 'child' || m.role === 'teen'
  const mitgliedId =
    (gewuenscht && auswahl.some((m) => m.id === gewuenscht) ? gewuenscht : null) ??
    (mitPlan.some((m) => m.id === ich) ? ich : null) ??
    mitPlan[0]?.id ??
    auswahl.find((m) => m.id === ich && schulpflichtig(m))?.id ??
    auswahl.find(schulpflichtig)?.id ??
    auswahl[0]?.id ??
    null
  const plaeneDesKindes = alle.filter((p) => p.memberId === mitgliedId)
  const plan = plaeneDesKindes.find((p) => p.id === planId) ?? aktuellerPlan(plaeneDesKindes, heute)
  const person = personen.find((m) => m.id === mitgliedId)

  const detail = useQuery({
    queryKey: ['timetable', 'plan', plan?.id],
    queryFn: () => hole<{ timetable: Plan }>(`/api/timetable/${plan?.id ?? ''}`),
    enabled: plan !== undefined,
  })
  const stunden = detail.data?.timetable.lessons ?? []
  const wocheJetzt = plan ? wocheHeute(plan, heute) : null
  const woche = plan?.weekPattern === 'ab' ? (gewaehlteWoche ?? wocheJetzt) : null
  const proTag = stundenProTag(stunden, woche)

  const meldeFehler = (e: unknown, sonst: string) =>
    setFehler(e instanceof ApiFehler ? e.message : sonst)
  const frisch = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['timetable'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
      qc.invalidateQueries({ queryKey: ['activity'] }),
    ])
  const waehleMitglied = (id: string) => {
    setPlanId(null)
    setGewaehlteWoche(null)
    setParams({ mitglied: id })
  }

  // ── Plan anlegen ───────────────────────────────────────────────────────
  const [neu, setNeu] = useState<NeuerPlan | null>(null)
  const schulwoche =
    vorlagen.data?.templates.find((v) => v.name === 'Schulwoche') ?? vorlagen.data?.templates[0]

  const planBody = (n: NeuerPlan) => ({
    memberId: n.memberId,
    name: n.name.trim(),
    validFrom: n.validFrom,
    validUntil: n.validUntil || null,
    weekPattern: n.weekPattern,
    abAnchorMonday: n.weekPattern === 'ab' ? n.abAnchorMonday || null : null,
  })
  const anlegen = useMutation({
    mutationFn: ({ n, vorlage }: { n: NeuerPlan; vorlage: string | null }) =>
      vorlage
        ? schicke<{ timetable: Plan }>('POST', '/api/timetable/from-template', {
            templateId: vorlage,
            ...planBody(n),
          })
        : schicke<{ timetable: Plan }>('POST', '/api/timetable', planBody(n)),
    onSuccess: async (r, v) => {
      setNeu(null)
      setFehler(null)
      await frisch()
      setPlanId(r.timetable.id)
      setParams({ mitglied: v.n.memberId })
    },
    onError: (e: unknown) => meldeFehler(e, 'Der Stundenplan konnte nicht angelegt werden.'),
  })

  // ── Plan bearbeiten / archivieren ──────────────────────────────────────
  const [bearbeiten, setBearbeiten] = useState<{ id: string; form: NeuerPlan } | null>(null)
  const [zuLoeschen, setZuLoeschen] = useState<{ id: string; name: string } | null>(null)
  const aendern = useMutation({
    mutationFn: ({ id, n }: { id: string; n: NeuerPlan }) =>
      schicke('PATCH', `/api/timetable/${id}`, planBody(n)),
    onSuccess: async () => {
      setBearbeiten(null)
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Der Stundenplan konnte nicht geändert werden.'),
  })
  const archivieren = useMutation({
    mutationFn: (id: string) => schicke('DELETE', `/api/timetable/${id}`),
    onSuccess: async () => {
      setPlanId(null)
      setBearbeiten(null)
      setZuLoeschen(null)
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Der Stundenplan konnte nicht archiviert werden.'),
  })

  // ── Stunden ────────────────────────────────────────────────────────────
  const [stunde, setStunde] = useState<NeueStunde | null>(null)
  const speichereStunden = useMutation({
    mutationFn: async ({ id, liste }: { id: string; liste: Record<string, unknown>[] }) =>
      schicke<{ lessons: Stunde[] }>('PUT', `/api/timetable/${id}/lessons`, { lessons: liste }),
    onSuccess: async () => {
      setStunde(null)
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Die Stunden konnten nicht gespeichert werden.'),
  })
  const stundeEintragen = useMutation({
    mutationFn: async (s: NeueStunde) => {
      if (!plan) throw new Error('Kein Plan')
      let subjectId = s.subjectId
      if (!subjectId) {
        const f = await schicke<{ subject: Fach }>('POST', '/api/timetable/subjects', {
          name: s.neuesFach.trim(),
        })
        subjectId = f.subject.id
      }
      const neueStunde = {
        weekday: s.weekday,
        week: plan.weekPattern === 'ab' && s.week ? s.week : null,
        startsAt: s.startsAt,
        endsAt: s.endsAt,
        subjectId,
        room: s.room.trim() || null,
        note: null,
      }
      return schicke<{ lessons: Stunde[] }>('PUT', `/api/timetable/${plan.id}/lessons`, {
        lessons: [...alsStundenBody(stunden), neueStunde],
      })
    },
    onSuccess: async () => {
      setStunde((s) => (s ? { ...LEERE_STUNDE, weekday: s.weekday, week: s.week } : null))
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Die Stunde konnte nicht eingetragen werden.'),
  })
  const entfernen = (id: string) => {
    if (!plan) return
    speichereStunden.mutate({
      id: plan.id,
      liste: alsStundenBody(stunden.filter((s) => s.id !== id)),
    })
  }

  const alsForm = (p: Plan): NeuerPlan => ({
    memberId: p.memberId,
    name: p.name,
    validFrom: p.validFrom,
    validUntil: p.validUntil ?? '',
    weekPattern: p.weekPattern,
    abAnchorMonday: p.abAnchorMonday ?? '',
  })

  // Die Haupthandlung hängt am Zustand: ohne Plan legt man einen an, mit Plan
  // trägt man Stunden ein. Zwei schwebende Knöpfe gibt es nicht.
  useFab(
    darfAendern && plan
      ? {
          label: 'Stunde eintragen',
          icon: 'plus',
          onClick: () => {
            setStunde({ ...LEERE_STUNDE, week: '' })
          },
        }
      : darfAnlegen && !plan
        ? {
            label: 'Stundenplan anlegen',
            icon: 'plus',
            onClick: () => {
              setNeu({
                // ⚠️ Das GEWÄHLTE Kind, nicht `kandidaten[0]`: /api/members
                // sortiert nicht, der erste Eintrag ist also willkürlich. Auf
                // Produktion sind so zwei Pläne beim Erwachsenen gelandet
                // statt beim Kind.
                memberId: mitgliedId ?? kandidaten[0]?.id ?? '',
                name: 'Schuljahr',
                validFrom: heute,
                validUntil: '',
                weekPattern: 'single',
                abAnchorMonday: '',
              })
            },
          }
        : null,
  )

  if (plaene.isError || mitglieder.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void plaene.refetch()
            void mitglieder.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Stundenplan</h1>
          <p>Wer hat wann Schule — mit A/B-Wochen und Schuljahr.</p>
        </div>
      </div>

      {fehler ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      {auswahl.length > 1 ? (
        <div className="chips" role="group" aria-label="Kind">
          {auswahl.map((m) => (
            <Chip
              key={m.id}
              gewaehlt={m.id === mitgliedId}
              onClick={() => {
                waehleMitglied(m.id)
              }}
            >
              {m.displayName}
            </Chip>
          ))}
        </div>
      ) : null}

      {plaene.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {!plaene.isPending && !plan ? (
        <Empty
          titel={
            person ? `Noch kein Stundenplan für ${person.displayName}` : 'Noch kein Stundenplan'
          }
          text={
            darfAnlegen
              ? 'Der Knopf unten rechts legt einen an — die Vorlage „Schulwoche" bringt das Zeitraster schon mit.'
              : 'Sobald jemand einen Stundenplan anlegt, steht er hier.'
          }
        />
      ) : null}

      {plan ? (
        <>
          <Card
            titel={`${person?.displayName ?? 'Stundenplan'} · ${plan.name}`}
            aktion={
              darfAendern || darfAnlegen || darfArchivieren ? (
                <div className="reihe">
                  {darfAnlegen ? (
                    <Button
                      variant="text"
                      onClick={() => {
                        setNeu({
                          memberId: mitgliedId ?? kandidaten[0]?.id ?? '',
                          name: 'Schuljahr',
                          validFrom: heute,
                          validUntil: '',
                          weekPattern: 'single',
                          abAnchorMonday: '',
                        })
                      }}
                    >
                      Neuer Plan
                    </Button>
                  ) : null}
                  {darfAendern ? (
                    <Button
                      variant="text"
                      onClick={() => {
                        setBearbeiten({ id: plan.id, form: alsForm(plan) })
                      }}
                    >
                      Plan bearbeiten
                    </Button>
                  ) : null}
                  {darfArchivieren ? (
                    <Button
                      variant="text"
                      className="btn--gefahr"
                      onClick={() => {
                        setZuLoeschen({ id: plan.id, name: plan.name })
                      }}
                    >
                      Plan löschen
                    </Button>
                  ) : null}
                </div>
              ) : undefined
            }
          >
            <p className="unterzeile">
              {gueltigkeitText(plan)}
              {plan.weekPattern === 'ab' && wocheJetzt ? ` · diese Woche ist ${wocheJetzt}` : ''}
            </p>

            {plaeneDesKindes.length > 1 ? (
              <Select
                id="plan-wahl"
                label="Schuljahr"
                wert={plan.id}
                onWert={(w) => {
                  setPlanId(w)
                }}
              >
                {plaeneDesKindes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({gueltigkeitText(p)})
                  </option>
                ))}
              </Select>
            ) : null}

            {plan.weekPattern === 'ab' ? (
              <SegmentedButtons<Woche>
                label="Woche"
                wert={woche ?? 'A'}
                optionen={(['A', 'B'] as const).map((w) => ({
                  wert: w,
                  label: `Woche ${w}${w === wocheJetzt ? ' (jetzt)' : ''}`,
                }))}
                onWert={(w) => {
                  setGewaehlteWoche(w)
                }}
              />
            ) : null}
          </Card>

          {detail.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

          {sichtbareTage(stunden).map((tag) => {
            const liste = proTag.get(tag) ?? []
            return (
              <Card key={tag} titel={WOCHENTAG_NAME[tag]}>
                {liste.length === 0 ? (
                  <p className="unterzeile">Frei.</p>
                ) : (
                  <ul className="liste liste--gruppe">
                    {liste.map((s) => (
                      <ListItem
                        key={s.id}
                        titel={s.subject?.name ?? 'Fach'}
                        unterzeile={`${zeitSpanne(s)}${s.room ? ` · ${s.room}` : ''}${
                          s.week ? ` · nur Woche ${s.week}` : ''
                        }`}
                        farbe={s.subject?.color ?? undefined}
                        folgend={
                          darfAendern ? (
                            <IconButton
                              name="trash"
                              label={`${s.subject?.name ?? 'Stunde'} ${WOCHENTAG_NAME[tag]} ${s.startsAt} entfernen`}
                              disabled={speichereStunden.isPending}
                              onClick={() => {
                                entfernen(s.id)
                              }}
                            />
                          ) : undefined
                        }
                      />
                    ))}
                  </ul>
                )}
              </Card>
            )
          })}

          <Sheet
            offen={stunde !== null}
            titel="Stunde eintragen"
            onSchliessen={() => {
              setStunde(null)
            }}
          >
            {stunde ? (
              <form
                className="stack-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  stundeEintragen.mutate(stunde)
                }}
              >
                <div className="reihe reihe--felder">
                  <Select
                    id="st-tag"
                    label="Tag"
                    wert={String(stunde.weekday)}
                    onWert={(w) => {
                      setStunde({ ...stunde, weekday: Number(w) })
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((t) => (
                      <option key={t} value={t}>
                        {WOCHENTAG_NAME[t]}
                      </option>
                    ))}
                  </Select>
                  {plan.weekPattern === 'ab' ? (
                    <Select
                      id="st-woche"
                      label="Woche"
                      wert={stunde.week}
                      onWert={(w) => {
                        setStunde({ ...stunde, week: w as '' | Woche })
                      }}
                    >
                      <option value="">jede Woche</option>
                      <option value="A">nur A</option>
                      <option value="B">nur B</option>
                    </Select>
                  ) : null}
                </div>
                <div className="reihe reihe--felder">
                  <TextField
                    id="st-von"
                    label="Von"
                    type="time"
                    wert={stunde.startsAt}
                    required
                    onWert={(w) => {
                      setStunde({ ...stunde, startsAt: w })
                    }}
                  />
                  <TextField
                    id="st-bis"
                    label="Bis"
                    type="time"
                    wert={stunde.endsAt}
                    required
                    onWert={(w) => {
                      setStunde({ ...stunde, endsAt: w })
                    }}
                  />
                </div>
                <Select
                  id="st-fach"
                  label="Fach"
                  wert={stunde.subjectId}
                  onWert={(w) => {
                    setStunde({ ...stunde, subjectId: w })
                  }}
                >
                  <option value="">— neues Fach —</option>
                  {(faecher.data?.subjects ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
                {!stunde.subjectId ? (
                  <TextField
                    id="st-neu"
                    label="Neues Fach"
                    wert={stunde.neuesFach}
                    hilfe="Mathe"
                    maxLength={60}
                    required
                    onWert={(w) => {
                      setStunde({ ...stunde, neuesFach: w })
                    }}
                  />
                ) : null}
                <TextField
                  id="st-raum"
                  label="Raum"
                  wert={stunde.room}
                  hilfe="R12"
                  maxLength={40}
                  onWert={(w) => {
                    setStunde({ ...stunde, room: w })
                  }}
                />
                <div className="blatt__fuss-inline">
                  <Button
                    variant="text"
                    onClick={() => {
                      setStunde(null)
                    }}
                  >
                    Fertig
                  </Button>
                  <Button
                    type="submit"
                    laeuft={stundeEintragen.isPending}
                    disabled={
                      (!stunde.subjectId && !stunde.neuesFach.trim()) ||
                      stunde.endsAt <= stunde.startsAt
                    }
                  >
                    Eintragen
                  </Button>
                </div>
              </form>
            ) : null}
          </Sheet>
        </>
      ) : null}

      <Sheet
        offen={bearbeiten !== null}
        titel="Plan bearbeiten"
        onSchliessen={() => {
          setBearbeiten(null)
        }}
      >
        {bearbeiten ? (
          <PlanForm
            titel="Plan bearbeiten"
            form={bearbeiten.form}
            kandidaten={kandidaten}
            laeuft={aendern.isPending}
            onChange={(form) => {
              setBearbeiten({ ...bearbeiten, form })
            }}
            onAbbruch={() => {
              setBearbeiten(null)
            }}
            aktionen={[
              {
                label: 'Speichern',
                primaer: true,
                onKlick: () => {
                  aendern.mutate({ id: bearbeiten.id, n: bearbeiten.form })
                },
              },
            ]}
          />
        ) : null}
      </Sheet>

      <Sheet
        offen={zuLoeschen !== null}
        titel="Stundenplan löschen"
        onSchliessen={() => {
          setZuLoeschen(null)
        }}
      >
        {zuLoeschen ? (
          <div className="stack-4">
            <p>
              Soll „{zuLoeschen.name}“ wirklich gelöscht werden? Der Plan und seine Stunden werden
              anschließend nicht mehr angezeigt.
            </p>
            <div className="blatt__fuss-inline">
              <Button
                variant="text"
                onClick={() => {
                  setZuLoeschen(null)
                }}
              >
                Abbrechen
              </Button>
              <Button
                variant="filled"
                className="btn--gefahr"
                laeuft={archivieren.isPending}
                onClick={() => {
                  archivieren.mutate(zuLoeschen.id)
                }}
              >
                Plan löschen
              </Button>
            </div>
          </div>
        ) : null}
      </Sheet>

      <Sheet
        offen={neu !== null}
        titel="Neuer Stundenplan"
        onSchliessen={() => {
          setNeu(null)
        }}
      >
        {neu ? (
          <PlanForm
            titel="Neuer Stundenplan"
            form={neu}
            kandidaten={kandidaten}
            laeuft={anlegen.isPending}
            onChange={setNeu}
            onAbbruch={() => {
              setNeu(null)
            }}
            aktionen={[
              ...(schulwoche
                ? [
                    {
                      label: 'Aus Vorlage „Schulwoche"',
                      primaer: true,
                      onKlick: () => {
                        anlegen.mutate({ n: neu, vorlage: schulwoche.id })
                      },
                    },
                  ]
                : []),
              {
                label: 'Leer anlegen',
                primaer: !schulwoche,
                onKlick: () => {
                  anlegen.mutate({ n: neu, vorlage: null })
                },
              },
            ]}
          />
        ) : null}
      </Sheet>
    </>
  )
}

function PlanForm({
  titel,
  form,
  kandidaten,
  laeuft,
  onChange,
  onAbbruch,
  aktionen,
  extra,
}: {
  titel: string
  form: NeuerPlan
  kandidaten: MitgliedKurz[]
  laeuft: boolean
  onChange: (f: NeuerPlan) => void
  onAbbruch: () => void
  aktionen: { label: string; primaer: boolean; onKlick: () => void }[]
  extra?: React.ReactNode
}) {
  const bereit =
    form.memberId !== '' &&
    form.name.trim() !== '' &&
    form.validFrom !== '' &&
    (form.weekPattern !== 'ab' || form.abAnchorMonday !== '')
  return (
    <form
      className="stack-4"
      aria-label={titel}
      onSubmit={(e) => {
        e.preventDefault()
        aktionen.find((a) => a.primaer)?.onKlick()
      }}
    >
      <div className="reihe reihe--felder">
        <Select
          id="pl-kind"
          label="Für wen"
          wert={form.memberId}
          onWert={(w) => {
            onChange({ ...form, memberId: w })
          }}
        >
          {kandidaten.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </Select>
        <TextField
          id="pl-name"
          label="Name"
          wert={form.name}
          required
          maxLength={80}
          hilfe="Schuljahr 26/27"
          onWert={(w) => {
            onChange({ ...form, name: w })
          }}
        />
      </div>
      <div className="reihe reihe--felder">
        <TextField
          id="pl-von"
          label="Gilt ab"
          type="date"
          wert={form.validFrom}
          required
          onWert={(w) => {
            onChange({ ...form, validFrom: w })
          }}
        />
        <TextField
          id="pl-bis"
          label="Gilt bis"
          type="date"
          wert={form.validUntil}
          onWert={(w) => {
            onChange({ ...form, validUntil: w })
          }}
        />
      </div>
      <div className="reihe reihe--felder">
        <Select
          id="pl-muster"
          label="Wochen"
          wert={form.weekPattern}
          onWert={(w) => {
            onChange({ ...form, weekPattern: w === 'ab' ? 'ab' : 'single' })
          }}
        >
          <option value="single">jede Woche gleich</option>
          <option value="ab">A/B-Wochen</option>
        </Select>
        {form.weekPattern === 'ab' ? (
          <TextField
            id="pl-anker"
            label="Montag einer A-Woche"
            type="date"
            wert={form.abAnchorMonday}
            required
            onWert={(w) => {
              onChange({ ...form, abAnchorMonday: w })
            }}
          />
        ) : null}
      </div>
      <div className="blatt__fuss-inline">
        <div className="reihe">
          <Button variant="text" onClick={onAbbruch}>
            Abbrechen
          </Button>
          {extra}
        </div>
        <div className="reihe">
          {aktionen.map((a) => (
            <Button
              key={a.label}
              variant={a.primaer ? 'filled' : 'tonal'}
              type={a.primaer ? 'submit' : 'button'}
              laeuft={laeuft}
              disabled={!bereit}
              {...(a.primaer ? {} : { onClick: a.onKlick })}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </div>
    </form>
  )
}
